import { Component, HostListener, Inject, OnDestroy, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { WhatsappTemplatesService } from 'src/app/parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { TableColumnTypeEnum, TableFilterTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';

import { TableLabelCellEnum } from 'src/app/shared/components/table/shared/models/table-label-cell.enum';
import { KakaotalkService, WhatsappService } from 'src/app/shared/strategies/channels';
import { EngagementHistoryService } from './engagement-history.service';
import { Engagement } from './shared/models/engagement.model';
import { EngagementUserIdTypeEnum } from 'src/app/shared/components/choose-template-flow/shared/engagement-user-id-type.enum';

@Component({
  selector: 'app-engagement-history',
  templateUrl: './engagement-history.component.html',
  styleUrls: ['./engagement-history.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EngagementHistoryComponent implements OnDestroy {
  selectedTabIndex = 0;
  config: TableConfig = {
    displayedColumns: [
      'recipient_id',
      'recipient_name',
      'recipient_external_id',
      'status',
      'message',
      'date',
      'creator',
      'account',
      'templateUsed',
      'read',
      'actions',
    ],
    columns: [
      { name: 'recipient_id', translationKey: 'engagement/history/recipient-id', property: 'recipient_id' },
      {
        name: 'recipient_name',
        translationKey: 'engagement/history/recipient-name',
        property: 'recipient_name',
        type: TableColumnTypeEnum.STRONG,
      },
      {
        name: 'recipient_external_id',
        translationKey: 'engagement/history/recipient-social-id',
        property: 'recipient_external_id',
      },
      {
        name: 'status',
        translationKey: 'engagement/history/status',
        property: 'statusLabel',
        type: TableColumnTypeEnum.LABEL,
      },
      { name: 'message', translationKey: 'engagement/history/message', property: 'message' },
      {
        name: 'date',
        translationKey: 'engagement/history/date-sent',
        property: 'date',
        type: TableColumnTypeEnum.DATE,
        sortEnabled: true,
      },
      { name: 'creator', translationKey: 'engagement/history/sent-by', property: 'creator' },
      {
        name: 'account',
        translationKey: 'engagement/history/account',
        property: 'site',
        type: TableColumnTypeEnum.SITE,
      },
      { name: 'templateUsed', translationKey: 'engagement/history/template-used', property: 'templateLabel' },
      { name: 'read', translationKey: 'engagement/history/read', property: 'readLabel' },
    ],
    actions: [
      {
        tooltip: 'see-details',
        clickFn: (element: Engagement) => this.seeConversation(element.id),
        icon: 'visibility',
      },
    ],
  };
  filters: TableFilters = {
    status: {
      name: 'sendStatus',
      items: [],
      placeholder: 'engagement/history/filters/status',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
    creator: {
      name: 'sentBy',
      items: [],
      placeholder: 'engagement/history/filters/sent-by',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
    templateUsed: {
      name: 'templateIds',
      items: [],
      placeholder: 'engagement/history/filters/template-used',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
    date: {
      name: 'sendDateRange',
      placeholder: 'engagement/history/filters/date-sent',
      type: TableFilterTypeEnum.DATE,
    },
  };
  errorDisplayed: {
    code: number;
    title: string;
  };

  statusesChoices: Choice[] = [
    { id: 'sent', label: this.basicService.translate('engagement/history/status/sent') },
    { id: 'sending', label: this.basicService.translate('engagement/history/status/sending') },
    { id: 'delivered', label: this.basicService.translate('engagement/history/status/delivered') },
    { id: 'failed', label: this.basicService.translate('engagement/history/status/failed') },
  ];
  usersChoices: Choice[] = [];
  templatesChoices: Choice[] = [];
  allowedSiteTypes = [this.whatsappService.getType(), this.kakaotalkService.getType()];
  CapabilityIdEnum = CapabilityIdEnum;
  private subscriptions = new Subscription();

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }
  @ViewChild('errorTemplateRef') errorTemplateRef: TemplateRef<any>;

  constructor(
    public dialog: MatDialog,
    private basicService: BasicService,
    private sitesService: SitesService,
    private engagementHistoryService: EngagementHistoryService,
    private searchBarService: SearchBarService,
    private customRouter: CustomRouter,
    private whatsappService: WhatsappService,
    private kakaotalkService: KakaotalkService,
    alcmeonContextService: AlcmeonContextService,
    whatsappTemplatesService: WhatsappTemplatesService,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    this.topToolbarService.setTitleTranslation('engagement/history/sent-messages');
    this.searchBarService.sort = { active: 'date', direction: 'desc' };
    this.searchBarService.referer$ = this.engagementHistoryService.retrieveEngagements;
    this.searchBarService.formatter = (element: Engagement) => {
      element.readLabel = this.basicService.translate(element.read ? 'yes' : 'no');
      element.site = this.sitesService.enrichSiteWithLogo(this.sitesService.sitesV2Map.get(element.account));
      element.message = element.template?.body;

      let color = null;
      switch (element.status) {
        case 'failed':
          color = TableLabelCellEnum.RED;
          break;
        case 'sent':
          color = TableLabelCellEnum.GREEN;
          break;
        case 'delivered':
          color = TableLabelCellEnum.GREY;
          break;
        case 'sending':
          color = TableLabelCellEnum.YELLOW;
          break;
      }
      element.statusLabel = {
        text: basicService.translate(`engagement/history/status/${element.status}`),
        color,
      };

      element.templateLabel =
        element.template?.whatsapp_template_name || basicService.translate('engagement/history/template-not-found');
      element.showError = this.showError;
    };
    this.filters.status.items = this.statusesChoices;
    this.usersChoices = alcmeonContextService.self.companies[0].users.map(({ id, realname }) => ({
      id: id,
      label: realname,
    }));
    this.filters.creator.items = this.usersChoices;
    whatsappTemplatesService.findAll().subscribe(list => {
      this.templatesChoices = list.map(({ metadata }) => {
        const { id, name } = metadata;
        return { id, label: name };
      });
      this.filters.templateUsed.items = this.templatesChoices;
    });

    this.searchBarService.submitQuery();
  }

  primaryButtonAction = () => {
    this.customRouter.go('auth.alcmeon.engagement.send-message.search');
  };

  showError = (error: { code: number; title: string }) => {
    this.errorDisplayed = error;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'medium-size',
      data: {
        title: this.basicService.translate('engagement/history/error-detail/title'),
        description: this.basicService.translate('engagement/history/error-detail/description'),
        templateRef: this.errorTemplateRef,
        cancelBtnText: this.basicService.translate('close'),
      },
    });
    dialogRef.afterClosed().subscribe(() => (this.errorDisplayed = null));
  };

  seeConversation = (messageSentId: number) => {
    const message: Engagement = this.searchBarService.itemsBS.value.find(({ id }) => id === messageSentId);
    if (message) {
      const { recipient_external_id, account, recipient_external_id_type } = message;

      let userExternalId: string;

      switch (recipient_external_id_type || EngagementUserIdTypeEnum.PHONE_NUMBER) {
        case EngagementUserIdTypeEnum.PHONE_NUMBER:
          userExternalId = recipient_external_id;
          break;
        case EngagementUserIdTypeEnum.APP_USER_ID:
          userExternalId = `,${recipient_external_id}`;
          break;
        case EngagementUserIdTypeEnum.USER_KEY:
          userExternalId = `,,${recipient_external_id}`;
          break;
        default:
          userExternalId = recipient_external_id;
          break;
      }

      this.customRouter.go('auth.alcmeon.engagement.send-message.user-external-id', {
        userExternalId,
        siteId: account,
      });
    }
  };

  ngOnDestroy(): void {
    this.topToolbarService.clearTitle();
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
  }
}
